// @ts-nocheck -- inherited from lift-teams; pre-existing type issues outside this port's scope
import {
  Button,
  SpinnerLoader,
  Toastify,
  Sparkles,
  Info,
  ChevronDown,
  ChevronUp,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  EyeOff,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  MultiSelectCheckboxTag,
} from 'lift-ui';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { useSelector } from 'react-redux';

import { AlertFormData, BrandConfig } from '../types';
import {
  dmgAlertApiService,
  SlackUser,
  SlackChannelWithDetails,
} from '../utils/dmgAlertApiService';
import {
  convertHtmlToSlackMarkdown,
  convertHtmlToEmailHtml,
  convertHtmlToPlainText,
  stripHtmlToPlainText,
} from '../utils/helpers';
import { buildThresholdConfigs } from '../utils/mappers';

import { SlackMessageRenderer } from './SlackMessageRenderer';
import '../../../styles/dmg-alert-system.styles.css';

// These components are no longer used but kept for potential future use
// Uncomment if needed
/*
interface NotificationChannelPillsProps {
  selectedChannel: string;
  onChannelChange: (channel: string) => void;
}

export const NotificationChannelPills: React.FC<NotificationChannelPillsProps> = ({
  selectedChannel,
  onChannelChange,
}) => {
  const channels = ['Slack', 'Email', 'Lift Notification'];

  return (
    <div className="space-y-4">
      <label className="text-sm font-medium text-gray-700 block text-left">
        Notification Channel
      </label>
      <div className="flex gap-3">
        {channels.map(channel => (
          <button
            key={channel}
            onClick={() => onChannelChange(channel)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              selectedChannel === channel
                ? 'bg-gray-200 text-gray-800'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-150'
            }`}
          >
            {channel}
          </button>
        ))}
      </div>
    </div>
  );
};

interface AlertMessageInputProps {
  value: string;
  onChange: (value: string) => void;
  onClear: () => void;
}

export const AlertMessageInput: React.FC<AlertMessageInputProps> = ({
  value,
  onChange,
  onClear,
}) => {
  return (
    <div className="space-y-4">
      <label className="text-sm font-medium text-gray-700 block text-left">Alert Message</label>
      <div className="flex gap-3">
        <div className="flex-1">
          <textarea
            value={value}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => onChange(e.target.value)}
            placeholder="Enter a concise alert message..."
            className="w-full min-h-20 px-3 py-2 border border-blue-500 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            rows={3}
          />
        </div>
        <div className="flex flex-col gap-2">
          <Button variant="outline" onClick={onClear} className="text-gray-600">
            Clear
          </Button>
          <Button className="flex items-center gap-2">
            <Sparkles className="h-4 w-4" />
            Generate via LIFT AI
          </Button>
        </div>
      </div>
    </div>
  );
};
*/

interface AlertTypeVariable {
  col: string;
  name: string;
  type: string;
}

interface VariableRow {
  key: string;
  name: string;
  type: string;
  placeholder?: string;
}

interface MessageTemplateStepProps {
  formData: AlertFormData;
  // eslint-disable-next-line no-unused-vars
  onInputChange: (field: keyof AlertFormData, value: string | number) => void;
  // eslint-disable-next-line no-unused-vars
  onMessageTemplateChange: (channel: 'slack' | 'email' | 'web', content: string) => void;
  userId?: number;
  selectedBrands?: BrandConfig[];
  alertTypes?: Array<{
    id: number;
    name: string;
    display_name: string;
    description: string;
    data_source: string;
    default_threshold_config?: Array<{
      value?: number;
      metric?: string;
      operator?: string;
      max_value?: string | number;
      period_days?: number;
      variables?: AlertTypeVariable[];
    }>;
    created_at: string;
    updated_at: string;
    alert_query_config?: {
      properties: string[];
    };
  }>;
  selectedCommunicationMethods?: string[];
  slackUsers?: SlackUser[];
  slackChannelsWithDetails?: SlackChannelWithDetails[];
  userEmail?: string;
}

export interface MessageTemplateStepRef {
  shouldBlockNavigation: () => Promise<boolean>;
  triggerPreview: () => Promise<void>;
  getPreviewState: () => { showPreview: boolean; isLoadingPreview: boolean };
}

export const MessageTemplateStep = React.forwardRef<
  MessageTemplateStepRef,
  MessageTemplateStepProps
>(
  (
    {
      formData,
      onInputChange,
      onMessageTemplateChange,
      userId,
      selectedBrands = [],
      alertTypes = [],
      selectedCommunicationMethods = [],
      slackUsers = [],
      slackChannelsWithDetails = [],
      userEmail,
    },
    ref
  ) => {
    // Get global selected brand from Redux
    const brandObject = useSelector(
      (state: { dashboard?: { brandObject?: { id?: number; brandId?: number } } }) =>
        state.dashboard?.brandObject || {}
    );

    const [isGenerating, setIsGenerating] = useState(false);
    const [isSendingTest, setIsSendingTest] = useState(false);
    const [isVariablesCollapsed, setIsVariablesCollapsed] = useState(true);
    const [showVar, setShowVar] = useState<{
      left: number;
      top: number;
      relativeTop?: number;
    } | null>(null);
    const [focusedIndex, setFocusedIndex] = useState(0);
    const focusedIndexRef = useRef(0);
    const [showCursor, setShowCursor] = useState(false);
    const justOpenedRef = useRef(false);
    const mentionListRef = useRef<HTMLDivElement>(null);
    const mentionItemRefs = useRef<(HTMLDivElement | null)[]>([]);
    const tempEditor = useRef<ReactQuill>(null);
    const tableRef = useRef<HTMLDivElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const [activeEditor, setActiveEditor] = useState<'textarea' | 'quill'>('quill');
    const [filterText, setFilterText] = useState('');
    const hasGeneratedWithAI = useRef(false);
    const hasInitialized = useRef(false);
    // Track if we've generated in this session (for first generation logic)
    const hasGeneratedInSession = useRef(false);
    // Store original values from database for first-time generation in edit mode
    const originalAlertMessage = useRef<string | undefined>(undefined);
    const originalTemplates = useRef<{ slack?: string; email?: string; web?: string } | undefined>(
      undefined
    );
    const [shouldAnimateGenerate, setShouldAnimateGenerate] = useState(false);
    const [showGenerateTooltip, setShowGenerateTooltip] = useState(false);
    const [showGenerateReflection, setShowGenerateReflection] = useState(false);
    const generateAttentionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const generateTooltipTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Track if alert message was changed and if it was generated after change
    const originalAlertMessageOnMount = useRef<string | undefined>(formData.alertMessage);
    const hasMessageChanged = useRef(false);
    const hasGeneratedAfterChange = useRef(false);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const pendingNavigationCallback = useRef<((proceed: boolean) => void) | null>(null);
    const [showPreview, setShowPreview] = useState(false);
    const [showChannelSelectionDialog, setShowChannelSelectionDialog] = useState(false);
    const [selectedTestChannels, setSelectedTestChannels] = useState<string[]>([]);
    const [isLoadingPreview, setIsLoadingPreview] = useState(false);
    const [previewContent, setPreviewContent] = useState<
      Array<{
        brand_id: number;
        brand_name: string;
        email?: string;
        slack?: string;
        web?: string;
        email_subject?: string;
        slack_table_block?: {
          type: string;
          rows: Array<
            Array<{
              type: string;
              text: string;
            }>
          >;
          column_settings: Array<{
            align: string;
            is_wrapped: boolean;
          }>;
        };
      }>
    >([]);

    const scheduleGenerateAttention = useCallback(() => {
      setShouldAnimateGenerate(false);
      setShowGenerateTooltip(false);
      setShowGenerateReflection(false);
      if (generateAttentionTimerRef.current) {
        clearTimeout(generateAttentionTimerRef.current);
      }
      if (generateTooltipTimerRef.current) {
        clearTimeout(generateTooltipTimerRef.current);
      }
      generateAttentionTimerRef.current = setTimeout(() => {
        if (!isGenerating) {
          setShouldAnimateGenerate(true);
          setShowGenerateTooltip(true);
          setShowGenerateReflection(true);
          generateTooltipTimerRef.current = setTimeout(() => {
            setShowGenerateTooltip(false);
          }, 4000);
        }
      }, 1000);
    }, [isGenerating]);

    useEffect(() => {
      return () => {
        if (generateAttentionTimerRef.current) {
          clearTimeout(generateAttentionTimerRef.current);
        }
        if (generateTooltipTimerRef.current) {
          clearTimeout(generateTooltipTimerRef.current);
        }
      };
    }, []);

    // Extract variables from the selected alert type
    const getVariablesFromAlertType = useCallback((): VariableRow[] => {
      if (!formData.toolName || !alertTypes.length) {
        return [];
      }

      const selectedAlertType = alertTypes.find(at => at.display_name === formData.toolName);
      if (!selectedAlertType?.default_threshold_config || !Array.isArray(selectedAlertType.default_threshold_config)) {
        return [];
      }

      const variables: VariableRow[] = [];
      selectedAlertType.default_threshold_config.forEach(config => {
        if (config.variables && Array.isArray(config.variables)) {
          config.variables.forEach(variable => {
            // Avoid duplicates
            if (!variables.find(v => v.key === variable.name)) {
              variables.push({
                key: variable.name,
                name: variable.name,
                type: variable.type,
                placeholder: variable.col === 'default' ? 'Default value' : variable.col,
              });
            }
          });
        }
      });

      return variables;
    }, [formData.toolName, alertTypes]);

    const [rows, setRows] = useState<VariableRow[]>([]);
    const [rowsBackup, setRowsBackup] = useState<VariableRow[]>([]);

    useEffect(() => {
      const variables = getVariablesFromAlertType();
      setRows(variables);
      setRowsBackup(variables);
    }, [getVariablesFromAlertType]);

    // Filter rows based on filterText
    useEffect(() => {
      if (!filterText) {
        setRows(rowsBackup);
      } else {
        const filtered = rowsBackup.filter(row =>
          row.key.toLowerCase().includes(filterText.toLowerCase())
        );
        setRows(filtered);
        // Reset focused index when filtering
        setFocusedIndex(0);
        focusedIndexRef.current = 0;
      }
    }, [filterText, rowsBackup]);

    // Helper function to convert plain text with \n to HTML for ReactQuill
    const convertNewlinesToHtml = useCallback((text: string): string => {
      if (!text) return '';

      // Convert markdown-style bold (**text**) to HTML <strong>
      let processedText = text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

      // Split by newlines and wrap each line in a <p> tag
      const lines = processedText.split('\n');
      const htmlLines = lines.map(line => {
        // If line is empty, use a <br> for proper spacing
        if (line.trim() === '') {
          return '<p><br></p>';
        }
        return `<p>${line}</p>`;
      });

      return htmlLines.join('');
    }, []);

    // Track if alert message changes from initial value
    useEffect(() => {
      if (hasInitialized.current) {
        const currentMessage = formData.alertMessage || '';
        const originalMessage = originalAlertMessageOnMount.current || '';

        if (currentMessage !== originalMessage) {
          hasMessageChanged.current = true;
          // Reset generated flag when message changes
          if (!hasGeneratedAfterChange.current) {
            hasGeneratedAfterChange.current = false;
          }
        } else {
          // If message is back to original, reset flags
          hasMessageChanged.current = false;
          hasGeneratedAfterChange.current = false;
        }
      }
    }, [formData.alertMessage]);

    // Initialize: Check if we're editing an existing alert with different templates
    // Store original values from database for first-time generation
    useEffect(() => {
      if (!hasInitialized.current) {
        // Store original alertMessage from database (for first-time generation)
        if (formData.alertMessage && formData.alertMessage.trim()) {
          originalAlertMessage.current = formData.alertMessage;
          originalAlertMessageOnMount.current = formData.alertMessage;
        }

        // Store original templates from database (for first-time generation)
        if (formData.messageTemplates) {
          const { slack, email, web } = formData.messageTemplates;

          // Convert HTML to plain text and store originals
          const originalTemplatesToStore: { slack?: string; email?: string; web?: string } = {};
          if (slack && slack.trim()) {
            originalTemplatesToStore.slack = convertHtmlToPlainText(slack);
          }
          if (email && email.trim()) {
            originalTemplatesToStore.email = convertHtmlToPlainText(email);
          }
          if (web && web.trim()) {
            originalTemplatesToStore.web = convertHtmlToPlainText(web);
          }

          if (Object.keys(originalTemplatesToStore).length > 0) {
            originalTemplates.current = originalTemplatesToStore;
          }

          // If templates exist and are different from each other, it means they were customized
          // (either by AI or manually edited)
          const hasCustomTemplates =
            (slack && slack.trim()) || (email && email.trim()) || (web && web.trim());

          const templatesAreDifferent = slack !== email || email !== web || slack !== web;

          // If we have custom templates or different templates, mark as AI-generated
          // to prevent auto-sync from overwriting them
          if (hasCustomTemplates || templatesAreDifferent) {
            hasGeneratedWithAI.current = true;
          }
        }

        // Mark as initialized even if messageTemplates is undefined (for new alerts)
        hasInitialized.current = true;
      }
    }, [formData.messageTemplates, formData.alertMessage]);

    // Sync short alert message to all channel templates (only if AI hasn't generated content yet)
    useEffect(() => {
      if (
        formData.alertMessage !== undefined &&
        !hasGeneratedWithAI.current &&
        hasInitialized.current
      ) {
        // Convert plain text to HTML format for ReactQuill
        const htmlContent = convertNewlinesToHtml(formData.alertMessage || '');

        // Only sync to all three channel templates if AI hasn't been used
        onMessageTemplateChange('slack', htmlContent);
        onMessageTemplateChange('email', htmlContent);
        onMessageTemplateChange('web', htmlContent);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.alertMessage, convertNewlinesToHtml]);

    // Quill modules configuration
    const modulesCustom = {
      toolbar: [
        [{ header: [1, 2, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['clean'],
      ],
    };

    // Quill formats configuration
    const formatsCustom = ['header', 'bold', 'italic', 'underline', 'strike', 'list', 'bullet'];

    // Filter channels based on selected communication methods from step 3
    // Map: '1' = Slack, '2' = Email, '3' or '4' = Lift Notification (web)
    const allChannels = [
      { key: 'Slack' as const, value: 'slack' as const, methodId: '1' },
      { key: 'Email' as const, value: 'email' as const, methodId: '2' },
      { key: 'Lift Notification' as const, value: 'web' as const, methodIds: ['3', '4'] },
    ];

    const channels = allChannels.filter(channel => {
      if (channel.methodId) {
        // Single method ID (Slack or Email)
        return selectedCommunicationMethods.includes(channel.methodId);
      } else if (channel.methodIds) {
        // Multiple method IDs (Lift Notification can be '3' or '4')
        return channel.methodIds.some(id => selectedCommunicationMethods.includes(id));
      }
      return false;
    });

    // Only show channels that are selected in step 3
    const visibleChannels = channels;

    // Determine active channel key for preview/tabs, defaulting to first visible channel if current is not available
    const activePreviewChannelKey = (() => {
      const currentKey = formData.notificationChannel;
      const isVisible = visibleChannels.some(ch => ch.key === currentKey);
      if (isVisible) {
        return currentKey;
      }
      return visibleChannels.length > 0 ? visibleChannels[0].key : 'Slack';
    })();

    // Determine current channel, defaulting to first visible channel if current is not available
    const getCurrentChannel = () => {
      const channelMap: Record<string, 'slack' | 'email' | 'web'> = {
        Slack: 'slack',
        Email: 'email',
        'Lift Notification': 'web',
      };
      return channelMap[activePreviewChannelKey] || 'slack';
    };

    const currentChannel = getCurrentChannel();

    const currentTemplate = formData.messageTemplates?.[currentChannel] || '';

    // Toggle variables collapse
    const toggleVariablesCollapse = () => {
      setIsVariablesCollapsed(prev => !prev);
    };

    // Handle variable selection from dropdown
    const handleVarSelection = useCallback(
      (row: VariableRow) => {
        if (activeEditor === 'textarea' && textareaRef.current) {
          const textarea = textareaRef.current;
          const start = textarea.selectionStart;
          const end = textarea.selectionEnd;
          const text = textarea.value;
          const beforeCursor = text.substring(0, start);
          const lastBraceIndex = beforeCursor.lastIndexOf('{');

          if (lastBraceIndex !== -1) {
            // Replace from { to cursor with the variable
            const newText =
              text.substring(0, lastBraceIndex) + `{${row.key}}` + text.substring(end);

            onInputChange('alertMessage', newText);

            // Set cursor position after the inserted variable
            setTimeout(() => {
              const newPosition = lastBraceIndex + row.key.length + 2;
              textarea.setSelectionRange(newPosition, newPosition);
              textarea.focus();
            }, 0);
          }
        } else if (tempEditor.current) {
          const editor = tempEditor.current.getEditor();
          const selection = editor.getSelection(true);
          if (!selection) return;

          // Find the { character and replace with variable
          const text = editor.getText();
          const beforeCursor = text.substring(0, selection.index);
          const lastBraceIndex = beforeCursor.lastIndexOf('{');

          if (lastBraceIndex !== -1) {
            // Replace from { to cursor with the variable
            editor.deleteText(lastBraceIndex, selection.index - lastBraceIndex);
            editor.insertText(lastBraceIndex, `{${row.key}}`, 'user');
            editor.setSelection(lastBraceIndex + row.key.length + 2);
          }
        }

        setShowVar(null);
        setFilterText('');
        setFocusedIndex(0);
        focusedIndexRef.current = 0;
        scheduleGenerateAttention();
        justOpenedRef.current = false;
      },
      [activeEditor, onInputChange, scheduleGenerateAttention]
    );

    // Detect a `{` typed in the Quill editor and open the variable dropdown.
    // Deferred to the next tick so Quill v2 (react-quill-new) has finished
    // syncing selection after the text-change event — otherwise typing `{`
    // into an empty editor sees a stale selection index of 0.
    const detectBraceInQuill = () => {
      if (!tempEditor.current) return;
      const editor = tempEditor.current.getEditor();

      const text = editor.getText();
      // Prefer Quill's selection; fall back to the length just before the
      // trailing newline so a fresh `{` at position 0 is still discovered.
      const quillSelection = editor.getSelection();
      const cursorIndex =
        quillSelection?.index ?? Math.max(0, editor.getLength() - 1);
      const beforeCursor = text.substring(0, cursorIndex);
      const lastBraceIndex = beforeCursor.lastIndexOf('{');

      if (lastBraceIndex === -1) {
        setShowVar(null);
        setFilterText('');
        return;
      }

      const afterBrace = beforeCursor.substring(lastBraceIndex + 1);
      if (afterBrace.includes('}') || afterBrace.includes(' ')) {
        setShowVar(null);
        setFilterText('');
        return;
      }

      const editorRoot = editor.root as HTMLElement;
      const editorRect = editorRoot.getBoundingClientRect();
      // Quill v2 (react-quill-new) can return null from getBounds, and with
      // preserveWhitespace it can return a zero-sized rect. Fall back to the
      // native DOM selection so the dropdown still anchors near the caret.
      const bounds = editor.getBounds(cursorIndex);
      let popupLeft: number;
      let popupTop: number;
      let popupRelativeTop: number;
      if (bounds && (bounds.height > 0 || bounds.width > 0)) {
        popupLeft = editorRect.left + bounds.left;
        popupTop = editorRect.top + bounds.top + bounds.height;
        popupRelativeTop = bounds.top + bounds.height;
      } else {
        const domSelection = window.getSelection();
        const domRange =
          domSelection && domSelection.rangeCount > 0
            ? domSelection.getRangeAt(0)
            : null;
        const caretRect = domRange?.getBoundingClientRect();
        if (caretRect && (caretRect.height > 0 || caretRect.width > 0)) {
          popupLeft = caretRect.left;
          popupTop = caretRect.top + caretRect.height;
          popupRelativeTop = caretRect.top + caretRect.height - editorRect.top;
        } else {
          popupLeft = editorRect.left + 8;
          popupTop = editorRect.top + 24;
          popupRelativeTop = 24;
        }
      }
      setActiveEditor('quill');
      setFilterText(afterBrace);
      setShowVar({
        left: popupLeft,
        top: popupTop,
        relativeTop: popupRelativeTop,
      });
      setFocusedIndex(0);
      focusedIndexRef.current = 0;
      justOpenedRef.current = true;
      // Reset the flag after a short delay to allow mouse events
      setTimeout(() => {
        justOpenedRef.current = false;
      }, 100);
    };

    // Handle editor change and detect { character
    const handleEditorChange = (content: string) => {
      onMessageTemplateChange(currentChannel, content);
      scheduleGenerateAttention();
      // Defer so Quill finishes updating its selection before we read it.
      setTimeout(detectBraceInQuill, 0);
    };

    // Handle textarea change and detect { character
    const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const value = e.target.value;
      onInputChange('alertMessage', value);
      scheduleGenerateAttention();

      // Reset generated flag when message changes
      if (hasMessageChanged.current) {
        hasGeneratedAfterChange.current = false;
      }

      if (!textareaRef.current) return;

      const textarea = textareaRef.current;
      const start = textarea.selectionStart;
      const text = value;
      const beforeCursor = text.substring(0, start);
      const lastBraceIndex = beforeCursor.lastIndexOf('{');

      if (lastBraceIndex !== -1) {
        const afterBrace = beforeCursor.substring(lastBraceIndex + 1);
        // Check if there's no closing brace and no space after {
        if (!afterBrace.includes('}') && !afterBrace.includes(' ')) {
          // Get cursor position in the textarea using a mirror div technique
          // This accounts for soft line wraps (visual wrapping without \n)
          const textareaRect = textarea.getBoundingClientRect();
          const computedStyle = window.getComputedStyle(textarea);

          // Create a hidden mirror div to measure actual cursor position
          const mirror = document.createElement('div');
          mirror.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            visibility: hidden;
            white-space: pre-wrap;
            word-wrap: break-word;
            overflow-wrap: break-word;
            width: ${computedStyle.width};
            font-family: ${computedStyle.fontFamily};
            font-size: ${computedStyle.fontSize};
            line-height: ${computedStyle.lineHeight};
            padding: ${computedStyle.padding};
            border: ${computedStyle.border};
            box-sizing: ${computedStyle.boxSizing};
          `;

          // Insert text up to cursor with an inline span marker at the end
          const textBeforeCursor = beforeCursor;
          const escapedText = textBeforeCursor
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/\n/g, '<br>');
          mirror.innerHTML =
            escapedText + '<span id="cursor-marker" style="display:inline;"></span>';

          document.body.appendChild(mirror);

          const marker = mirror.querySelector('#cursor-marker');
          const markerRect = marker?.getBoundingClientRect();

          // The marker position is relative to viewport (0,0) since mirror is at top:0, left:0
          const relativeLeft = markerRect ? markerRect.left : 0;
          const relativeTop = markerRect ? markerRect.top : 0;

          // Clean up
          document.body.removeChild(mirror);

          const popupWidth = 200; // Approximate popup width
          const popupMargin = 10; // Margin from edge
          // Parse line height, handle "normal" case
          let lineHeight = parseFloat(computedStyle.lineHeight);
          if (isNaN(lineHeight)) {
            lineHeight = parseFloat(computedStyle.fontSize) * 1.2 || 20;
          }

          // Calculate position: textarea position + relative position within the mirror
          let calculatedLeft = textareaRect.left + relativeLeft;
          const calculatedTop = textareaRect.top + relativeTop + lineHeight;

          // Check if popup would go off-screen on the right
          const viewportWidth = window.innerWidth;
          if (calculatedLeft + popupWidth > viewportWidth - popupMargin) {
            // Position popup so it stays within viewport
            calculatedLeft = Math.max(textareaRect.left, viewportWidth - popupWidth - popupMargin);
          }

          setActiveEditor('textarea');
          setFilterText(afterBrace);
          setShowVar({
            left: calculatedLeft,
            top: calculatedTop,
          });
          setFocusedIndex(0);
          focusedIndexRef.current = 0;
          justOpenedRef.current = true;
          // Reset the flag after a short delay to allow mouse events
          setTimeout(() => {
            justOpenedRef.current = false;
          }, 100);
        } else {
          setShowVar(null);
          setFilterText('');
        }
      } else {
        setShowVar(null);
        setFilterText('');
      }
    };

    // Handle keyboard navigation in variable dropdown
    useEffect(() => {
      if (!showVar || rows.length === 0) return;

      const handleKeyDown = (e: KeyboardEvent) => {
        if (showVar === null) return;

        if (e.key === 'ArrowDown') {
          e.preventDefault();
          e.stopPropagation();
          setFocusedIndex(prev => {
            const newIndex = prev < rows.length - 1 ? prev + 1 : prev;
            focusedIndexRef.current = newIndex;
            return newIndex;
          });
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          e.stopPropagation();
          setFocusedIndex(prev => {
            const newIndex = prev > 0 ? prev - 1 : 0;
            focusedIndexRef.current = newIndex;
            return newIndex;
          });
        } else if (e.key === 'Enter' || e.key === 'Tab') {
          e.preventDefault();
          e.stopPropagation();
          const currentIndex = focusedIndexRef.current;
          if (rows[currentIndex]) {
            handleVarSelection(rows[currentIndex]);
          }
        } else if (e.key === 'Escape') {
          e.preventDefault();
          e.stopPropagation();
          setShowVar(null);
          setFocusedIndex(0);
          focusedIndexRef.current = 0;
          justOpenedRef.current = false;
        }
      };

      window.addEventListener('keydown', handleKeyDown, true);
      return () => window.removeEventListener('keydown', handleKeyDown, true);
    }, [showVar, rows, handleVarSelection]);

    // Scroll focused item into view
    useEffect(() => {
      if (mentionItemRefs.current[focusedIndex] && mentionListRef.current) {
        mentionItemRefs.current[focusedIndex]?.scrollIntoView({
          block: 'nearest',
          behavior: 'smooth',
        });
      }
    }, [focusedIndex]);

    // Close variable dropdown when clicking outside the active editor/textarea
    useEffect(() => {
      if (!showVar) return;

      const handleClickOutside = (event: MouseEvent) => {
        const target = event.target as Node | null;
        if (!target) return;

        if (mentionListRef.current?.contains(target)) {
          return;
        }

        if (activeEditor === 'textarea') {
          if (textareaRef.current?.contains(target)) {
            return;
          }
        } else {
          const quillEditor = tempEditor.current ? tempEditor.current.getEditor() : null;
          const quillRoot = quillEditor ? (quillEditor.root as Node) : null;
          if (quillRoot?.contains(target)) {
            return;
          }
        }

        setShowVar(null);
        setFilterText('');
        setFocusedIndex(0);
        focusedIndexRef.current = 0;
        justOpenedRef.current = false;
      };

      document.addEventListener('mousedown', handleClickOutside, true);
      return () => document.removeEventListener('mousedown', handleClickOutside, true);
    }, [showVar, activeEditor]);

    // Build content string from form data for AI prompt
    const buildContentString = (): string => {
      const parts: string[] = [];

      // Alert name and description
      if (formData.alertName) {
        parts.push(`Alert Name: ${formData.alertName}`);
      }
      if (formData.description) {
        parts.push(`Description: ${formData.description}`);
      }

      // Tool/Alert Type
      if (formData.toolName) {
        parts.push(`Tool/Alert Type: ${formData.toolName}`);
      }

      // Brands and thresholds
      if (selectedBrands && selectedBrands.length > 0) {
        parts.push('\nBrands and Thresholds:');
        selectedBrands.forEach(brand => {
          parts.push(`- Brand: ${brand.brandName}`);
          if (brand.thresholds && brand.thresholds.length > 0) {
            brand.thresholds.forEach(threshold => {
              const thresholdInfo: string[] = [];
              if (threshold.pixelName) thresholdInfo.push(`Pixel: ${threshold.pixelName}`);
              if (threshold.eventName) thresholdInfo.push(`Event: ${threshold.eventName}`);
              thresholdInfo.push(`Operator: ${threshold.operator}`);
              thresholdInfo.push(`Threshold: ${threshold.threshold}`);
              parts.push(`  * ${thresholdInfo.join(', ')}`);
            });
          }
        });
      }

      // Cadence
      if (formData.cadence) {
        parts.push(`\nCadence: ${formData.cadence}`);
      }

      // Schedule config
      if (formData.scheduleConfig) {
        const scheduleParts: string[] = [];
        if (formData.scheduleConfig.time)
          scheduleParts.push(`Time: ${formData.scheduleConfig.time}`);
        if (formData.scheduleConfig.timezone)
          scheduleParts.push(`Timezone: ${formData.scheduleConfig.timezone}`);
        if (formData.scheduleConfig.days)
          scheduleParts.push(`Days: ${formData.scheduleConfig.days.join(', ')}`);
        if (formData.scheduleConfig.day) scheduleParts.push(`Day: ${formData.scheduleConfig.day}`);
        if (scheduleParts.length > 0) {
          parts.push(`Schedule: ${scheduleParts.join(', ')}`);
        }
      }

      // Current alert message (if any)
      if (formData.alertMessage) {
        parts.push(`\nCurrent Alert Message: ${formData.alertMessage}`);
      }

      // Available template variables
      if (rowsBackup && rowsBackup.length > 0) {
        parts.push('\nAvailable Template Variables:');
        parts.push(
          'You can use these variables in the message templates by wrapping them in curly braces, e.g., {variable_name}'
        );
        rowsBackup.forEach(variable => {
          const varInfo: string[] = [];
          varInfo.push(`Variable: {${variable.key}}`);
          if (variable.type) varInfo.push(`Type: ${variable.type}`);
          if (variable.placeholder)
            varInfo.push(`Placeholder/Description: ${variable.placeholder}`);
          parts.push(`- ${varInfo.join(', ')}`);
        });
        parts.push(
          '\nIMPORTANT: When generating message templates, make sure to use these variables appropriately in the messages. Use the variable names exactly as shown (case-sensitive) wrapped in curly braces.'
        );
      }

      return parts.join('\n');
    };

    // Get current user's Slack info
    const getCurrentUserSlackInfo = useCallback(() => {
      if (!userEmail || slackUsers.length === 0) return null;
      const slackUser = slackUsers.find(
        user => user.email?.toLowerCase() === userEmail.toLowerCase()
      );
      if (!slackUser) return null;
      return {
        user_id: userId || null,
        recipient_id: slackUser.slack_id,
        recipient_type: 'slack_user' as const,
        recipient_value: slackUser.real_name || slackUser.name,
      };
    }, [userEmail, slackUsers, userId]);

    // Send test notification with optional channel recipients
    const handleSendTestNotification = async (includeChannels: boolean = false) => {
      if (!userId) {
        Toastify('error', 'User ID is required to send test notification');
        return;
      }

      // Determine which channels are selected based on communication methods
      const isSlackSelected = selectedCommunicationMethods.includes('1');
      const isEmailSelected = selectedCommunicationMethods.includes('2');
      const isWebBrandSelected = selectedCommunicationMethods.includes('3');
      const isWebUserSelected = selectedCommunicationMethods.includes('4');
      const isWebSelected = selectedCommunicationMethods.some(id => id === '3' || id === '4');

      // Validate that selected channels have templates (check for both null/undefined and empty strings)
      const missingTemplates: string[] = [];
      if (
        isSlackSelected &&
        (!formData.messageTemplates?.slack || formData.messageTemplates.slack.trim() === '')
      ) {
        missingTemplates.push('Slack');
      }
      if (
        isEmailSelected &&
        (!formData.messageTemplates?.email || formData.messageTemplates.email.trim() === '')
      ) {
        missingTemplates.push('Email');
      }
      if (
        isWebSelected &&
        (!formData.messageTemplates?.web || formData.messageTemplates.web.trim() === '')
      ) {
        missingTemplates.push('Lift Notification');
      }

      if (missingTemplates.length > 0) {
        Toastify('error', `Please fill in message templates for: ${missingTemplates.join(', ')}`);
        return;
      }

      const alertTypeId = formData.alertTypeId;
      if (!alertTypeId) {
        Toastify('error', 'Alert type is required to send test notification');
        return;
      }

      // Build Slack recipients - always include current user
      const slackRecipients: Array<{
        user_id: number | null;
        recipient_id: string;
        recipient_type: 'slack_user' | 'slack_channel';
        recipient_value: string;
      }> = [];

      // Add current user's Slack info if Slack is selected
      if (isSlackSelected) {
        const currentUserSlack = getCurrentUserSlackInfo();
        if (currentUserSlack) {
          slackRecipients.push(currentUserSlack);
        }

        // Add selected channels if includeChannels is true
        if (includeChannels && selectedTestChannels.length > 0) {
          selectedTestChannels.forEach(channelId => {
            const channel = slackChannelsWithDetails.find(c => c.id === channelId);
            if (channel) {
              slackRecipients.push({
                user_id: null,
                recipient_id: channel.id,
                recipient_type: 'slack_channel',
                recipient_value: channel.name,
              });
            }
          });
        }
      }

      const emailRecipients: Array<{
        user_id: number | null;
        recipient_id: number | null;
        recipient_type: 'email_user';
        recipient_value: string;
      }> = isEmailSelected
        ? (formData.emailRecipients ?? [])
            .map(value => {
              try {
                const parsed = JSON.parse(value);
                if (parsed && parsed.email) {
                  const recipientId = parsed?.id ?? parsed?.user_id ?? null;
                  return {
                    user_id: recipientId,
                    recipient_id: recipientId,
                    recipient_type: 'email_user' as const,
                    recipient_value: parsed.email,
                  };
                }
              } catch {
                // Treat non-JSON value as raw email string.
              }
              return {
                user_id: null,
                recipient_id: null,
                recipient_type: 'email_user' as const,
                recipient_value: value,
              };
            })
            .filter(recipient => !!recipient.recipient_value)
        : [];

      if (isEmailSelected && emailRecipients.length === 0 && (formData.emailId || userEmail)) {
        emailRecipients.push({
          user_id: userId,
          recipient_id: userId,
          recipient_type: 'email_user',
          recipient_value: formData.emailId || userEmail || '',
        });
      }

      const webBrandRecipients: Array<{
        user_id: number;
        recipient_id: number;
        brand_id: number;
        brand_name: string;
        recipient_type: 'web_brand';
        recipient_value: {
          brand_id: number;
        };
      }> = isWebBrandSelected
        ? (selectedBrands ?? []).map(brand => ({
            user_id: userId,
            recipient_id: brand.brandId,
            brand_id: brand.brandId,
            brand_name: brand.brandName ?? 'Brand',
            recipient_type: 'web_brand' as const,
            recipient_value: { brand_id: brand.brandId },
          }))
        : [];

      const webUserRecipients: Array<{
        user_id: number;
        recipient_id: number;
        recipient_type: 'web_user';
        recipient_value: {
          name: string;
          user_id: number;
          email?: string;
        };
      }> = isWebUserSelected
        ? (formData.liftNotificationUserRecipients ?? [])
            .map(value => {
              try {
                const parsed = JSON.parse(value);
                const recipientId = parsed?.id ?? parsed?.user_id;
                if (!recipientId) return null;

                return {
                  user_id: recipientId,
                  recipient_id: recipientId,
                  recipient_type: 'web_user' as const,
                  recipient_value: {
                    name: parsed?.name || 'User',
                    user_id: recipientId,
                    email: parsed?.email,
                  },
                };
              } catch {
                return null;
              }
            })
            .filter((recipient): recipient is NonNullable<typeof recipient> => recipient !== null)
        : [];

      const webRecipients = [...webBrandRecipients, ...webUserRecipients];

      const recipientsPayload: {
        slack?: typeof slackRecipients;
        email?: typeof emailRecipients;
        web?: typeof webRecipients;
      } = {};
      if (isSlackSelected && slackRecipients.length > 0) {
        recipientsPayload.slack = slackRecipients;
      }
      if (isEmailSelected) {
        recipientsPayload.email = emailRecipients;
      }
      if (isWebSelected) {
        recipientsPayload.web = webRecipients;
      }

      setIsSendingTest(true);
      try {
        // Build list of channels being sent for user feedback
        const channelsBeingSent: string[] = [];
        if (isSlackSelected) channelsBeingSent.push('Slack');
        if (isEmailSelected) channelsBeingSent.push('Email');
        if (isWebSelected) channelsBeingSent.push('Lift Notification');

        // Determine brand_id: if one brand selected, use that; if multiple, use global selected brand
        let brandId: number | undefined;
        if (selectedBrands.length === 1) {
          brandId = selectedBrands[0].brandId;
        } else if (selectedBrands.length > 1 && brandObject?.id) {
          brandId =
            typeof brandObject.id === 'string' ? parseInt(brandObject.id, 10) : brandObject.id;
        }

        const selectedBrandIds =
          selectedBrands
            ?.map(brand => brand.brandId)
            .filter((id): id is number => typeof id === 'number') || [];

        let resolvedBrandIds = [...selectedBrandIds];
        if (resolvedBrandIds.length === 0 && brandId !== undefined) {
          resolvedBrandIds = [brandId];
        }

        const thresholdConfigs = buildThresholdConfigs(formData, selectedBrands, resolvedBrandIds);

        const result = await dmgAlertApiService.sendTestMessageNotification({
          userId,
          messageTemplate: {
            email_template: isEmailSelected
              ? convertHtmlToEmailHtml(formData.messageTemplates?.email || '')
              : '',
            slack_template: isSlackSelected
              ? convertHtmlToSlackMarkdown(formData.messageTemplates?.slack || '')
              : '',
            web_template: isWebSelected
              ? convertHtmlToEmailHtml(formData.messageTemplates?.web || '')
              : '',
          },
          typeId: alertTypeId,
          brandId,
          brandIds: resolvedBrandIds,
          thresholdConfigs,
          alertName: formData.alertName,
          recipients: Object.keys(recipientsPayload).length > 0 ? recipientsPayload : undefined,
        });

        if (result.success) {
          let successMessage = 'Test notification sent successfully';
          if (includeChannels && selectedTestChannels.length > 0) {
            const channelNames = selectedTestChannels
              .map(id => slackChannelsWithDetails.find(c => c.id === id)?.name)
              .filter(Boolean);
            successMessage += ` to you and channels: ${channelNames.join(', ')}`;
          } else {
            successMessage += ' to you';
          }
          Toastify('success', successMessage);

          // Close dialog and reset selections if it was open
          if (includeChannels) {
            setShowChannelSelectionDialog(false);
            setSelectedTestChannels([]);
          }
        } else {
          Toastify('error', result.message || 'Failed to send test notification');
        }
      } catch (error: unknown) {
        console.error('Error sending test notification:', error);
        const errorMessage =
          error instanceof Error ? error.message : 'Failed to send test notification';
        Toastify('error', errorMessage);
      } finally {
        setIsSendingTest(false);
      }
    };

    const handleTogglePreview = useCallback(async () => {
      // If preview is currently shown, just hide it
      if (showPreview) {
        setShowPreview(false);
        return;
      }

      // Otherwise, fetch the preview
      const alertTypeId = formData.alertTypeId;
      if (!alertTypeId) {
        Toastify('error', 'Alert type is required to preview template');
        return;
      }

      // Get brand IDs
      const selectedBrandIds =
        selectedBrands
          ?.map(brand => brand.brandId)
          .filter((id): id is number => typeof id === 'number') || [];

      if (selectedBrandIds.length === 0) {
        // Try to use global selected brand
        if (brandObject?.id) {
          const brandId =
            typeof brandObject.id === 'string' ? parseInt(brandObject.id, 10) : brandObject.id;
          selectedBrandIds.push(brandId);
        }
      }

      if (selectedBrandIds.length === 0) {
        Toastify('error', 'At least one brand is required to preview template');
        return;
      }

      // Determine which channels are selected
      const isSlackSelected = selectedCommunicationMethods.includes('1');
      const isEmailSelected = selectedCommunicationMethods.includes('2');
      const isWebSelected = selectedCommunicationMethods.some(id => id === '3' || id === '4');

      // Build message template object
      const messageTemplate: {
        email_template?: string;
        slack_template?: string;
        web_template?: string;
      } = {};

      if (isEmailSelected && formData.messageTemplates?.email) {
        messageTemplate.email_template = convertHtmlToEmailHtml(formData.messageTemplates.email);
      }
      if (isSlackSelected && formData.messageTemplates?.slack) {
        messageTemplate.slack_template = convertHtmlToSlackMarkdown(
          formData.messageTemplates.slack
        );
      }
      if (isWebSelected && formData.messageTemplates?.web) {
        messageTemplate.web_template = convertHtmlToEmailHtml(formData.messageTemplates.web);
      }

      if (Object.keys(messageTemplate).length === 0) {
        Toastify('error', 'Please fill in at least one message template to preview');
        return;
      }

      // Build threshold configs for preview
      const thresholdConfigs = buildThresholdConfigs(formData, selectedBrands, selectedBrandIds);

      setIsLoadingPreview(true);
      try {
        const result = await dmgAlertApiService.previewTemplate({
          typeId: alertTypeId,
          brandId: selectedBrandIds[0],
          userId: userId!,
          messageTemplate,
          thresholdConfigs,
          includeTable: true,
          alertName: formData.alertName,
        });

        if (result.success && result.previews.length > 0) {
          setPreviewContent(result.previews);
          setShowPreview(true);
        } else {
          Toastify('error', result.message || 'Failed to load preview');
        }
      } catch (error: unknown) {
        console.error('Error loading preview:', error);
        const errorMessage = error instanceof Error ? error.message : 'Failed to load preview';
        Toastify('error', errorMessage);
      } finally {
        setIsLoadingPreview(false);
      }
    }, [showPreview, formData, selectedBrands, brandObject?.id, selectedCommunicationMethods]);

    // Helper function to get localStorage key for storing previous content
    const getStorageKey = (): string => {
      if (!userId) return '';
      // Use alert name and user ID to create a unique key
      const alertName = formData.alertName || 'new-alert';
      return `dmg-alert-templates-${userId}-${alertName}`;
    };

    // Helper function to get previous content and templates from localStorage
    const getPreviousContent = (): string | undefined => {
      const storageKey = getStorageKey();
      if (!storageKey) return undefined;

      try {
        const stored = localStorage.getItem(storageKey);
        if (stored) {
          const parsed = JSON.parse(stored);
          return parsed.previousContent;
        }
      } catch (error) {
        console.error('Error reading from localStorage:', error);
      }
      return undefined;
    };

    // Helper function to get previous templates from localStorage
    const getPreviousTemplates = ():
      | { slack?: string; email?: string; web?: string }
      | undefined => {
      const storageKey = getStorageKey();
      if (!storageKey) return undefined;

      try {
        const stored = localStorage.getItem(storageKey);
        if (stored) {
          const parsed = JSON.parse(stored);
          if (parsed.previousTemplates) {
            // Filter out empty strings - only include templates with content
            const templates: { slack?: string; email?: string; web?: string } = {};
            if (parsed.previousTemplates.slack && parsed.previousTemplates.slack.trim()) {
              templates.slack = parsed.previousTemplates.slack;
            }
            if (parsed.previousTemplates.email && parsed.previousTemplates.email.trim()) {
              templates.email = parsed.previousTemplates.email;
            }
            if (parsed.previousTemplates.web && parsed.previousTemplates.web.trim()) {
              templates.web = parsed.previousTemplates.web;
            }
            // Only return if at least one template has content
            return Object.keys(templates).length > 0 ? templates : undefined;
          }
        }
      } catch (error) {
        console.error('Error reading previous templates from localStorage:', error);
      }
      return undefined;
    };

    // Helper function to save content and templates to localStorage
    const saveContentToStorage = (content: string) => {
      const storageKey = getStorageKey();
      if (!storageKey) return;

      try {
        const dataToStore = {
          previousContent: content,
          timestamp: new Date().toISOString(),
        };
        localStorage.setItem(storageKey, JSON.stringify(dataToStore));
      } catch (error) {
        console.error('Error saving to localStorage:', error);
      }
    };

    // Helper function to save templates to localStorage
    // Templates should already be plain text from the API response
    const saveTemplatesToStorage = (templates: { slack: string; email: string; web: string }) => {
      const storageKey = getStorageKey();
      if (!storageKey) return;

      try {
        // Get existing data or create new object
        const existingData = localStorage.getItem(storageKey);
        let dataToStore: {
          previousContent?: string;
          previousTemplates?: { slack: string; email: string; web: string };
          timestamp: string;
        } = existingData ? JSON.parse(existingData) : {};

        // Save templates as-is (they're already plain text from API)
        // Only include templates that have content
        const templatesToSave: { slack?: string; email?: string; web?: string } = {};
        if (templates.slack) templatesToSave.slack = templates.slack;
        if (templates.email) templatesToSave.email = templates.email;
        if (templates.web) templatesToSave.web = templates.web;

        if (Object.keys(templatesToSave).length > 0) {
          dataToStore.previousTemplates = templatesToSave as {
            slack: string;
            email: string;
            web: string;
          };
        }
        dataToStore.timestamp = new Date().toISOString();

        localStorage.setItem(storageKey, JSON.stringify(dataToStore));
      } catch (error) {
        console.error('Error saving templates to localStorage:', error);
      }
    };

    // Expose method to check if navigation should be blocked
    const shouldBlockNavigation = useCallback(async (): Promise<boolean> => {
      // Validate that all selected channels have message templates filled in
      // Use stripHtmlToPlainText to detect empty Quill content like <p><br></p>
      const missingChannels: string[] = [];
      if (
        selectedCommunicationMethods.includes('1') &&
        stripHtmlToPlainText(formData.messageTemplates?.slack).trim() === ''
      ) {
        missingChannels.push('Slack');
      }
      if (
        selectedCommunicationMethods.includes('2') &&
        stripHtmlToPlainText(formData.messageTemplates?.email).trim() === ''
      ) {
        missingChannels.push('Email');
      }
      if (
        selectedCommunicationMethods.some(id => id === '3' || id === '4') &&
        stripHtmlToPlainText(formData.messageTemplates?.web).trim() === ''
      ) {
        missingChannels.push('Lift Notification');
      }
      if (missingChannels.length > 0) {
        Toastify('error', `Please fill in message templates for: ${missingChannels.join(', ')}`);
        return false;
      }

      // If message changed and not generated after change, show dialog
      if (
        hasMessageChanged.current &&
        !hasGeneratedAfterChange.current &&
        formData.alertMessage &&
        formData.alertMessage.trim()
      ) {
        return new Promise<boolean>(resolve => {
          pendingNavigationCallback.current = resolve;
          setShowConfirmDialog(true);
        });
      }
      return true;
    }, [formData.alertMessage, formData.messageTemplates, selectedCommunicationMethods]);

    React.useImperativeHandle(
      ref,
      () => ({
        shouldBlockNavigation,
        triggerPreview: handleTogglePreview,
        getPreviewState: () => ({ showPreview, isLoadingPreview }),
      }),
      [shouldBlockNavigation, handleTogglePreview, showPreview, isLoadingPreview]
    );

    const handleGenerateWithAI = async () => {
      setShowGenerateReflection(false);
      if (generateAttentionTimerRef.current) {
        clearTimeout(generateAttentionTimerRef.current);
      }
      setShouldAnimateGenerate(false);

      if (!userId) {
        Toastify('error', 'User ID is required to generate messages');
        return;
      }

      // Build content string
      const content = buildContentString();

      if (!content || content.trim().length === 0) {
        Toastify('error', 'Please fill in alert details before generating messages');
        return;
      }

      setIsGenerating(true);
      try {
        // Get previous content and templates
        // For first-time generation in edit mode: use original values from database
        // For subsequent generations: use current formData values or localStorage
        let previousContent: string | undefined;
        let previousTemplates: { slack?: string; email?: string; web?: string } | undefined;

        // Check if this is the first generation in this session
        // If so, use original values from database (even if templates exist from previous edit)
        if (!hasGeneratedInSession.current) {
          // First generation: use original values from database
          previousContent = originalAlertMessage.current;
          previousTemplates = originalTemplates.current;
        } else {
          // Subsequent generations: use values from previous generation (stored in localStorage)
          // This ensures we use the templates/content from the last generation, not the current formData

          // First, try to get from localStorage (these are from the previous generation)
          previousContent = getPreviousContent();
          previousTemplates = getPreviousTemplates();

          // If localStorage doesn't have values, fall back to current formData (shouldn't happen normally)
          if (!previousContent && formData.alertMessage && formData.alertMessage.trim()) {
            previousContent = formData.alertMessage;
          }

          if (!previousTemplates && formData.messageTemplates) {
            const { slack, email, web } = formData.messageTemplates;
            const templatesToSend: { slack?: string; email?: string; web?: string } = {};
            if (slack && slack.trim()) {
              templatesToSend.slack = convertHtmlToPlainText(slack);
            }
            if (email && email.trim()) {
              templatesToSend.email = convertHtmlToPlainText(email);
            }
            if (web && web.trim()) {
              templatesToSend.web = convertHtmlToPlainText(web);
            }
            if (Object.keys(templatesToSend).length > 0) {
              previousTemplates = templatesToSend;
            }
          }
        }

        // The API service already parses the response and returns { slack, email, web }
        const templates = await dmgAlertApiService.generateMessageTemplates(
          userId,
          formData.alertMessage,
          previousContent,
          previousTemplates
        );

        // Mark that AI has been used
        hasGeneratedWithAI.current = true;
        // Mark that we've generated in this session
        hasGeneratedInSession.current = true;
        // Mark that we've generated after message change
        if (hasMessageChanged.current) {
          hasGeneratedAfterChange.current = true;
        }

        // Save current content to localStorage for next time (for previous_content API parameter)
        saveContentToStorage(formData.alertMessage);

        // Update message templates - convert \n to HTML for ReactQuill
        if (templates.slack) {
          onMessageTemplateChange('slack', convertNewlinesToHtml(templates.slack));
        }
        if (templates.email) {
          onMessageTemplateChange('email', convertNewlinesToHtml(templates.email));
        }
        if (templates.web) {
          onMessageTemplateChange('web', convertNewlinesToHtml(templates.web));
        }

        // Save generated templates (as plain text) to localStorage for next time (for previous_templates API parameter)
        // The API returns plain text templates, so we save them directly
        saveTemplatesToStorage(templates);

        Toastify('success', 'Message templates generated successfully!');
      } catch (error: unknown) {
        console.error('Error generating message templates:', error);
        const errorMessage =
          error instanceof Error ? error.message : 'Failed to generate message templates';
        Toastify('error', errorMessage);
      } finally {
        setIsGenerating(false);
      }
    };

    return (
      <div
        className="flex flex-col h-full"
        style={{
          maxWidth: '60rem',
          margin: '0 auto',
          minHeight: 'calc(90vh - 8rem)',
          marginBottom: '1.5rem',
        }}
      >
        {/* Fixed Header Section - Hidden when preview is shown */}
        {!showPreview && (
          <div className="flex-shrink-0 mb-6">
            <div className="flex items-end justify-between gap-4">
              <div className="flex-1">
                <div className="text-sm text-gray-600 mb-2">
                  Customize your alert message template. Use variables to insert dynamic content.
                </div>
              </div>
              <div className="flex-shrink-0 max-w-md">
                <div className="bg-[#DCE2FC] border rounded-xl p-3">
                  <div className="flex items-start gap-2">
                    <Info className="h-5 w-5 text-[#58504D] mt-0.5 flex-shrink-0" />
                    <div className="text-[#58504D] text-sm">
                      <strong>Quick tip:</strong> Type{' '}
                      <code className="bg-blue-100 px-1 rounded">{'{'}</code> in the editor to open
                      the variable list and insert dynamic content.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-y-auto pr-2 template-content-scrollable">
          <div className="space-y-6 template-content-area">
            {/* Show preview section only when showPreview is true */}
            {showPreview ? (
              /* Preview Panel - Full view when preview is shown */
              <div className="template-preview-section">
                <div className="border rounded-xl bg-white shadow-sm">
                  <div className="border-b bg-gray-50 px-4 py-3 rounded-t-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-base font-semibold text-gray-900">Template Preview</h3>
                        <p className="text-sm text-gray-600 mt-1">
                          Preview of your message templates with sample data.
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowPreview(false)}
                        className="text-gray-500 hover:text-gray-700"
                      >
                        <EyeOff className="h-4 w-4 mr-1" />
                        Back to Editor
                      </Button>
                    </div>
                  </div>
                  <div className="p-4 space-y-4">
                    {/* Channel Tabs for Preview */}
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex gap-0 bg-gray-100 p-1 rounded-lg w-fit">
                        {visibleChannels.map(channel => (
                          <button
                            key={channel.key}
                            onClick={() => {
                              onInputChange('notificationChannel', channel.key);
                            }}
                            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                              activePreviewChannelKey === channel.key
                                ? 'bg-white text-gray-800 shadow-sm'
                                : 'bg-transparent text-gray-600 hover:text-gray-800'
                            }`}
                          >
                            {channel.key}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Preview Content based on selected channel */}
                    {previewContent.map((brandPreview, index) => (
                      <div key={brandPreview.brand_id} className="space-y-4">
                        {/* Brand header - only show if multiple brands */}
                        {previewContent.length > 1 && (
                          <div className="flex items-center gap-2 pb-2 border-b">
                            <span className="text-sm font-semibold text-gray-800">
                              {brandPreview.brand_name}
                            </span>
                            <span className="text-xs text-gray-500">
                              (Brand ID: {brandPreview.brand_id})
                            </span>
                          </div>
                        )}

                        {/* Slack Preview - show only when Slack tab is selected */}
                        {activePreviewChannelKey === 'Slack' && brandPreview.slack && (
                          <div className="bg-gray-50 border rounded-lg p-4 space-y-4">
                            <SlackMessageRenderer message={brandPreview.slack} />
                            {/* Slack Table Block */}
                            {brandPreview.slack_table_block && (
                              <div className="mt-4 overflow-x-auto">
                                <div className="text-sm font-semibold text-gray-700 mb-3">
                                  Alert Details:
                                </div>
                                <table
                                  className="border-collapse text-sm bg-white rounded"
                                  style={{ tableLayout: 'auto' }}
                                >
                                  <thead>
                                    <tr className="bg-[#334238] text-white">
                                      {brandPreview.slack_table_block.rows[0]?.map(
                                        (header, colIndex) => {
                                          const colSettings =
                                            brandPreview.slack_table_block?.column_settings[
                                              colIndex
                                            ];
                                          const align =
                                            colSettings?.align === 'right'
                                              ? 'right'
                                              : colSettings?.align === 'center'
                                                ? 'center'
                                                : 'left';
                                          // First column can wrap, others use nowrap
                                          const isFirstCol = colIndex === 0;
                                          return (
                                            <th
                                              key={colIndex}
                                              className="border border-gray-300 px-3 py-2 text-left font-semibold"
                                              style={{
                                                textAlign: align,
                                                whiteSpace: isFirstCol ? 'normal' : 'nowrap',
                                                minWidth: isFirstCol ? '200px' : undefined,
                                                maxWidth: isFirstCol ? '250px' : undefined,
                                              }}
                                            >
                                              {header.text}
                                            </th>
                                          );
                                        }
                                      )}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {brandPreview.slack_table_block.rows
                                      .slice(1)
                                      .map((row, rowIndex) => (
                                        <tr
                                          key={rowIndex}
                                          className={rowIndex % 2 === 0 ? 'bg-gray-50' : 'bg-white'}
                                        >
                                          {row.map((cell, colIndex) => {
                                            const colSettings =
                                              brandPreview.slack_table_block?.column_settings[
                                                colIndex
                                              ];
                                            const align =
                                              colSettings?.align === 'right'
                                                ? 'right'
                                                : colSettings?.align === 'center'
                                                  ? 'center'
                                                  : 'left';
                                            // First column wraps text, others use nowrap
                                            const isFirstCol = colIndex === 0;
                                            return (
                                              <td
                                                key={colIndex}
                                                className="border border-gray-300 px-3 py-2"
                                                style={{
                                                  textAlign: align,
                                                  whiteSpace: isFirstCol ? 'normal' : 'nowrap',
                                                  minWidth: isFirstCol ? '200px' : undefined,
                                                  maxWidth: isFirstCol ? '250px' : undefined,
                                                }}
                                              >
                                                {cell.text}
                                              </td>
                                            );
                                          })}
                                        </tr>
                                      ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>
                        )}
                        {activePreviewChannelKey === 'Slack' && !brandPreview.slack && (
                          <div className="bg-gray-50 border rounded-lg p-4 text-sm text-gray-500 text-center">
                            No Slack preview available.
                          </div>
                        )}

                        {/* Email Preview - show only when Email tab is selected */}
                        {activePreviewChannelKey === 'Email' && brandPreview.email && (
                          <div className="space-y-2">
                            {brandPreview.email_subject && (
                              <div className="text-sm text-gray-600">
                                <span className="font-medium">Subject:</span>{' '}
                                {brandPreview.email_subject}
                              </div>
                            )}
                            <div
                              className="bg-white border rounded-lg p-4 text-sm [&_table]:block [&_table]:overflow-x-auto [&_table]:max-w-full [&_table_td]:whitespace-nowrap [&_table_th]:whitespace-nowrap [&_table_td:first-child]:whitespace-normal [&_table_td:first-child]:min-w-[200px] [&_table_td:first-child]:max-w-[250px] [&_table_th:first-child]:whitespace-normal"
                              dangerouslySetInnerHTML={{ __html: brandPreview.email }}
                            />
                          </div>
                        )}
                        {activePreviewChannelKey === 'Email' && !brandPreview.email && (
                          <div className="bg-gray-50 border rounded-lg p-4 text-sm text-gray-500 text-center">
                            No Email preview available.
                          </div>
                        )}

                        {/* Web/Lift Notification Preview - show only when Lift Notification tab is selected */}
                        {activePreviewChannelKey === 'Lift Notification' && brandPreview.web && (
                          <div
                            className="bg-gray-50 border rounded-lg p-4 text-sm [&_table]:block [&_table]:overflow-x-auto [&_table]:max-w-full [&_table_td]:whitespace-nowrap [&_table_th]:whitespace-nowrap [&_table_td:first-child]:whitespace-normal [&_table_td:first-child]:min-w-[200px] [&_table_td:first-child]:max-w-[250px] [&_table_th:first-child]:whitespace-normal"
                            dangerouslySetInnerHTML={{ __html: brandPreview.web }}
                          />
                        )}
                        {activePreviewChannelKey === 'Lift Notification' && !brandPreview.web && (
                          <div className="bg-gray-50 border rounded-lg p-4 text-sm text-gray-500 text-center">
                            No Lift Notification preview available.
                          </div>
                        )}

                        {/* Separator between brands */}
                        {previewContent.length > 1 && index < previewContent.length - 1 && (
                          <hr className="border-gray-200 mt-4" />
                        )}
                      </div>
                    ))}

                    {/* Show message if no preview content */}
                    {previewContent.length === 0 && (
                      <div className="text-center text-gray-500 py-4">
                        No preview available. Please ensure you have message templates configured.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <>
                {/* Variables Section - Moved to top */}
                <div className="template-variables-section">
                  <div className="border rounded-xl bg-white shadow-sm">
                    <div className="border-b bg-gray-50 px-4 py-3 rounded-t-lg">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-base font-semibold text-gray-900">
                            Template Variables
                          </h3>
                          <p className="text-sm text-gray-600 mt-1">
                            Manage variables that can be used in your alert message template.
                          </p>
                          <p className="text-sm text-gray-600 mt-1">
                            We have used placeholder values which are changeable for certain
                            variables.
                          </p>
                        </div>
                        <div className="flex items-center ml-4">
                          <Button
                            variant="default"
                            size="sm"
                            onClick={toggleVariablesCollapse}
                            className="flex items-center gap-1 collapse-button text-sm"
                          >
                            {isVariablesCollapsed ? (
                              <>
                                <ChevronDown className="h-3 w-3" />
                                Expand
                              </>
                            ) : (
                              <>
                                <ChevronUp className="h-3 w-3" />
                                Collapse
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    </div>
                    {!isVariablesCollapsed && (
                      <div className="p-2 variables-content">
                        <div className="variable-container overflow-hidden" ref={tableRef}>
                          <table className="w-full text-sm border-collapse">
                            <thead>
                              <tr className="bg-gray-50 border-b">
                                <th className="text-left p-2 font-semibold text-gray-700">
                                  Variable Name
                                </th>
                                <th className="text-left p-2 font-semibold text-gray-700">Type</th>
                                <th className="text-left p-2 font-semibold text-gray-700">
                                  Placeholder
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {rowsBackup.map((row, index) => (
                                <tr
                                  key={index}
                                  className="border-b hover:bg-gray-50 transition-colors"
                                >
                                  <td className="p-2">
                                    <code className="bg-gray-100 px-2 py-1 rounded text-xs">
                                      {`{${row.key}}`}
                                    </code>
                                  </td>
                                  <td className="p-2 text-gray-600">{row.type}</td>
                                  <td className="p-2 text-gray-600">{row.placeholder || '-'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Alert Message Input - Base message for AI generation */}
                <div className="space-y-3 w-full min-w-0 overflow-hidden">
                  <label className="text-sm font-medium text-gray-700 block text-left">
                    <div className="flex items-center gap-2">
                      <span>Alert Prompt Message</span>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="cursor-help">
                              <Info className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                            </div>
                          </TooltipTrigger>
                          <TooltipContent side="right" className="max-w-xs">
                            <p className="text-sm">
                              Enter a base message that describes your alert. This will be used as a
                              prompt to generate customized templates for all notification channels
                              (Slack, Email, and Lift Notification) using AI.
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  </label>
                  <div className="relative">
                    <textarea
                      ref={textareaRef}
                      value={formData.alertMessage || ''}
                      onChange={handleTextareaChange}
                      onKeyDown={e => {
                        if (showVar && activeEditor === 'textarea') {
                          if (
                            e.key === 'ArrowUp' ||
                            e.key === 'ArrowDown' ||
                            e.key === 'Tab' ||
                            e.key === 'Enter' ||
                            e.key === 'Escape'
                          ) {
                            e.preventDefault();
                            e.stopPropagation();
                          }
                        }
                      }}
                      onFocus={() => setActiveEditor('textarea')}
                      placeholder="Enter a concise alert message that will be used to generate templates for all channels..."
                      className="w-full min-h-[120px] px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:border-gray-400 resize-none min-w-0 max-w-full"
                      rows={5}
                      style={{
                        width: '100%',
                        maxWidth: '100%',
                        wordWrap: 'break-word',
                        overflowWrap: 'break-word',
                      }}
                    />
                    {showVar !== null && activeEditor === 'textarea' && (
                      <div>
                        <div className="mention-tag-remove"></div>
                        <div
                          ref={mentionListRef}
                          className="mention-list z-9999"
                          style={{
                            position: 'fixed',
                            left: showVar.left,
                            top: showVar.top,
                            zIndex: 1200,
                            cursor: 'none',
                          }}
                        >
                          {rows.length > 0 ? (
                            rows.map((row, i) => {
                              return (
                                <div
                                  key={i}
                                  ref={el => {
                                    mentionItemRefs.current[i] = el;
                                  }}
                                  className={`mention-user-item ${
                                    focusedIndex === i ? 'mention-user-item-focus' : ''
                                  } ${showCursor ? 'active' : 'inactive'}`}
                                  onClick={() => handleVarSelection(row)}
                                  onMouseMove={() => setShowCursor(true)}
                                  onMouseLeave={() => setShowCursor(false)}
                                  onMouseEnter={() => {
                                    // Don't update focus if dropdown just opened (prevent auto-focus on second item)
                                    if (!justOpenedRef.current) {
                                      setFocusedIndex(i);
                                      focusedIndexRef.current = i;
                                    }
                                  }}
                                >
                                  <span>{row.key}</span>
                                </div>
                              );
                            })
                          ) : (
                            <div className="mention-user-item">
                              <span>No matches found</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={() => {
                        onInputChange('alertMessage', '');
                        // Reset the AI generation flag when clearing
                        hasGeneratedWithAI.current = false;
                        hasInitialized.current = true; // Keep initialized to prevent issues
                        hasMessageChanged.current = false;
                        hasGeneratedAfterChange.current = false;
                        scheduleGenerateAttention();
                      }}
                      className="text-gray-600"
                      disabled={isGenerating}
                    >
                      Clear
                    </Button>
                    <TooltipProvider delayDuration={100}>
                      <Tooltip open={showGenerateTooltip}>
                        <TooltipTrigger asChild>
                          <div className="inline-flex relative">
                            <Button
                              variant="primary"
                              className="flex items-center gap-2 transition"
                              onClick={handleGenerateWithAI}
                              disabled={isGenerating}
                              style={
                                showGenerateReflection
                                  ? { animation: 'mt-float 2.4s ease-in-out infinite' }
                                  : undefined
                              }
                            >
                              {isGenerating ? (
                                <>
                                  <SpinnerLoader size={16} />
                                  Generating...
                                </>
                              ) : (
                                <>
                                  Generate via LIFT AI
                                  <Sparkles
                                    className={`h-4 w-4 transition ${
                                      shouldAnimateGenerate
                                        ? 'animate-pulse text-black'
                                        : 'text-black'
                                    }`}
                                  />
                                </>
                              )}
                            </Button>
                            {showGenerateReflection && (
                              <div className="pointer-events-none absolute inset-0 rounded-md mt-shimmer-effect mix-blend-screen" />
                            )}
                          </div>
                        </TooltipTrigger>
                        <TooltipContent className="text-xs" side="top" align="end">
                          Content updated - regenerate?
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </div>

                {/* Notification Channel Tabs */}
                <div className="space-y-3">
                  <label className="text-sm font-medium text-gray-700 block text-left">
                    Customize Message per Channel
                  </label>
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex gap-0 bg-gray-100 p-1 rounded-lg w-fit">
                      {visibleChannels.map(channel => (
                        <button
                          key={channel.key}
                          onClick={() => {
                            onInputChange('notificationChannel', channel.key);
                            scheduleGenerateAttention();
                          }}
                          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                            activePreviewChannelKey === channel.key
                              ? 'bg-white text-gray-800 shadow-sm'
                              : 'bg-transparent text-gray-600 hover:text-gray-800'
                          }`}
                        >
                          {channel.key}
                        </button>
                      ))}
                    </div>
                    {selectedCommunicationMethods.includes('1') ? (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="primary"
                            size="sm"
                            disabled={
                              isSendingTest ||
                              (selectedCommunicationMethods.includes('1') &&
                                (!formData.messageTemplates?.slack ||
                                  formData.messageTemplates.slack.trim() === '')) ||
                              (selectedCommunicationMethods.includes('2') &&
                                (!formData.messageTemplates?.email ||
                                  formData.messageTemplates.email.trim() === '')) ||
                              (selectedCommunicationMethods.some(id => id === '3' || id === '4') &&
                                (!formData.messageTemplates?.web ||
                                  formData.messageTemplates.web.trim() === ''))
                            }
                            className="flex items-center gap-1"
                          >
                            {isSendingTest ? (
                              <>
                                <SpinnerLoader size={14} />
                                <span className="ml-2">Sending...</span>
                              </>
                            ) : (
                              <>
                                Send Test Notification
                                <ChevronDown className="h-4 w-4 ml-1" />
                              </>
                            )}
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-56">
                          <DropdownMenuItem
                            onClick={() => handleSendTestNotification(false)}
                            disabled={isSendingTest}
                          >
                            Send to Myself
                          </DropdownMenuItem>
                          {slackChannelsWithDetails.length > 0 && (
                            <DropdownMenuItem
                              onClick={() => setShowChannelSelectionDialog(true)}
                              disabled={isSendingTest}
                            >
                              Send to Myself & Channels...
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    ) : (
                      <Button
                        variant="primary"
                        size="sm"
                        disabled={
                          isSendingTest ||
                          (selectedCommunicationMethods.includes('2') &&
                            (!formData.messageTemplates?.email ||
                              formData.messageTemplates.email.trim() === '')) ||
                          (selectedCommunicationMethods.some(id => id === '3' || id === '4') &&
                            (!formData.messageTemplates?.web ||
                              formData.messageTemplates.web.trim() === ''))
                        }
                        onClick={() => handleSendTestNotification(false)}
                        className="flex items-center gap-1"
                      >
                        {isSendingTest ? (
                          <>
                            <SpinnerLoader size={14} />
                            <span className="ml-2">Sending...</span>
                          </>
                        ) : (
                          'Send Test Notification'
                        )}
                      </Button>
                    )}
                  </div>
                </div>

                {/* Message Template Editor Section */}
                <div className="template-editor-section">
                  <div className="template-container relative border rounded-xl overflow-hidden bg-white">
                    <ReactQuill
                      key={currentChannel}
                      ref={tempEditor}
                      theme="snow"
                      modules={modulesCustom}
                      formats={formatsCustom}
                      value={currentTemplate}
                      preserveWhitespace={true}
                      onChange={handleEditorChange}
                      onKeyDown={e => {
                        if (showVar && activeEditor === 'quill') {
                          if (
                            e.key === 'ArrowUp' ||
                            e.key === 'ArrowDown' ||
                            e.key === 'Tab' ||
                            e.key === 'Enter' ||
                            e.key === 'Escape'
                          ) {
                            e.preventDefault();
                            e.stopPropagation();
                          }
                        }
                      }}
                      onFocus={() => setActiveEditor('quill')}
                      className="editor-quill-mail-full text-sm leading-[130%]"
                    />

                    {showVar !== null && activeEditor === 'quill' && (
                      <div>
                        <div className="mention-tag-remove"></div>
                        <div
                          ref={mentionListRef}
                          className="mention-list z-9999"
                          style={{
                            position: 'fixed',
                            left: showVar.left + 5,
                            top: showVar.top + 6,
                            zIndex: 1200,
                            cursor: 'none',
                          }}
                        >
                          {rows.length > 0 ? (
                            rows.map((row, i) => {
                              return (
                                <div
                                  key={i}
                                  ref={el => {
                                    mentionItemRefs.current[i] = el;
                                  }}
                                  className={`mention-user-item ${
                                    focusedIndex === i ? 'mention-user-item-focus' : ''
                                  } ${showCursor ? 'active' : 'inactive'}`}
                                  onClick={() => handleVarSelection(row)}
                                  onMouseMove={() => setShowCursor(true)}
                                  onMouseLeave={() => setShowCursor(false)}
                                  onMouseEnter={() => {
                                    // Don't update focus if dropdown just opened (prevent auto-focus on second item)
                                    if (!justOpenedRef.current) {
                                      setFocusedIndex(i);
                                      focusedIndexRef.current = i;
                                    }
                                  }}
                                >
                                  <span>{row.key}</span>
                                </div>
                              );
                            })
                          ) : (
                            <div className="mention-user-item">No matches found</div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Confirmation Dialog */}
        <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Continue without generating?</DialogTitle>
              <DialogDescription>
                You've changed the alert prompt message but haven't generated templates using LIFT
                AI. Are you sure you want to proceed without generating the message templates?
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setShowConfirmDialog(false);
                  if (pendingNavigationCallback.current) {
                    pendingNavigationCallback.current(false);
                    pendingNavigationCallback.current = null;
                  }
                }}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  setShowConfirmDialog(false);
                  if (pendingNavigationCallback.current) {
                    pendingNavigationCallback.current(true);
                    pendingNavigationCallback.current = null;
                  }
                }}
              >
                Continue without generating
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Channel Selection Dialog for Test Notification */}
        <Dialog open={showChannelSelectionDialog} onOpenChange={setShowChannelSelectionDialog}>
          <DialogContent className="sm:max-w-[400px]">
            <DialogHeader>
              <DialogTitle>Select Slack Channels</DialogTitle>
              <DialogDescription>
                Choose which channels to send the test notification to. The notification will always
                be sent to you as well.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              {slackChannelsWithDetails.length > 0 ? (
                <MultiSelectCheckboxTag
                  label="Select Channels"
                  placeholder="Select channels"
                  value={selectedTestChannels}
                  onChange={(values: string[]) => setSelectedTestChannels(values)}
                  options={slackChannelsWithDetails.map(channel => ({
                    label: `#${channel.name}${channel.is_private === 1 ? ' (private)' : ''}`,
                    value: channel.id,
                  }))}
                  containerClassName="w-full"
                />
              ) : (
                <div className="text-center text-gray-500 py-4">No channels available</div>
              )}
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setShowChannelSelectionDialog(false);
                  setSelectedTestChannels([]);
                }}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={() => handleSendTestNotification(true)}
                disabled={isSendingTest || selectedTestChannels.length === 0}
              >
                {isSendingTest ? (
                  <>
                    <SpinnerLoader size={14} />
                    <span className="ml-2">Sending...</span>
                  </>
                ) : (
                  `Send to ${selectedTestChannels.length > 0 ? selectedTestChannels.length : ''} Channel${selectedTestChannels.length !== 1 ? 's' : ''}`
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }
);